/**
 * i18n resources for the `usage` feature slice (usage/cost views + the
 * PII-redacted audit/activity feed).
 *
 * Shape: `{ namespace, resources: { en, bg, de, fr } }`. Non-English locales
 * start as English placeholders so the key-parity check passes; translators
 * replace the strings without changing the key shape (master prompt §13).
 */

export const namespace = 'usage' as const;

const en = {
  page: {
    eyebrow: 'Intelligence',
    title: 'Usage & cost',
    description: 'Per-call usage, rolled-up cost, and a redacted activity feed.',
  },
  summary: {
    title: 'Usage summary',
    description: 'Tenant-level usage rolls up here as calls complete.',
    emptyTitle: 'No usage yet',
    emptyDescription: 'Usage entries appear after your first completed call.',
    column: {
      type: 'Type',
      provider: 'Provider',
      quantity: 'Quantity',
      unitCost: 'Unit cost',
      totalCost: 'Total cost',
      when: 'When',
    },
    totalLabel: 'Total cost',
  },
  cost: {
    title: 'Call cost',
    description: 'Rolled-up cost for a single call.',
    callIdLabel: 'Call ID',
    callIdPlaceholder: 'Enter a call ID',
    lookupBtn: 'Look up',
    totalLabel: 'Total',
    emptyTitle: 'No call selected',
    emptyDescription: 'Enter a call ID to see its usage and cost.',
  },
  audit: {
    title: 'Activity feed',
    description: 'Newest-first, PII-redacted record of actions across the hotel.',
    emptyTitle: 'No activity yet',
    emptyDescription: 'Actions taken across the hotel will appear here.',
    column: {
      action: 'Action',
      actor: 'Actor',
      resource: 'Resource',
      outcome: 'Outcome',
      when: 'When',
    },
    outcome: {
      success: 'Success',
      failure: 'Failure',
      blocked: 'Blocked',
    },
    redactedNote: 'Sensitive details are redacted by design.',
    toolbar: {
      searchPlaceholder: 'Search action / resource',
      outcomeAll: 'All outcomes',
      clear: 'Clear filters',
    },
    emptyFilteredTitle: 'No matching activity',
    emptyFilteredDescription: 'No activity matches these filters. Try clearing them.',
    paging: {
      prev: 'Previous',
      next: 'Next',
      perPage: 'Rows per page',
      range: '{{from}}–{{to}} of {{total}}',
    },
  },
  states: {
    loading: 'Loading usage…',
    errorTitle: 'Could not load usage',
    errorDescription: 'Something went wrong loading usage. Please try again.',
    auditErrorTitle: 'Could not load activity',
    auditErrorDescription: 'Something went wrong loading the activity feed.',
  },
  noAccess: {
    title: 'No access',
    description: 'You do not have permission to view usage for this hotel.',
  },
  disabled: {
    title: 'Usage reporting is off',
    description: 'Usage reporting is not enabled for this hotel.',
  },
} as const;

export const resources = {
  en: { [namespace]: en },
  bg: { [namespace]: en },
  de: { [namespace]: en },
  fr: { [namespace]: en },
} as const;
